import { Boxes } from "lucide-react";
import * as React from "react";

import type { FlowSummary } from "@/api/flows";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

/**
 * Flow template picker — selectable card per Flow under the
 * configured flows directory. Single-row selection; click a card to
 * activate it. The host page binds ``selected`` to the manifest
 * fetch + the submission button.
 *
 * Per Rupert PR-261: arrow-key navigation matches the TilePicker
 * (roving tabindex + ↑↓←→ / Home / End) and fetch errors surface
 * inline instead of skeleton-forever.
 */
export interface FlowPickerProps {
  flows: FlowSummary[] | undefined;
  selected: string | null;
  onSelect: (name: string) => void;
  isLoading?: boolean;
  /** Pass the fetch error through so a 5xx on /api/flows surfaces
   * an inline error rather than the skeleton spinning forever. */
  error?: Error | null;
  className?: string;
}

export function FlowPicker({
  flows,
  selected,
  onSelect,
  isLoading,
  error,
  className,
}: FlowPickerProps): JSX.Element {
  const containerRef = React.useRef<HTMLDivElement | null>(null);

  if (error) {
    return (
      <div className="rounded-lg border border-state-failed-validation-border bg-state-failed-validation-bg p-6 text-center text-sm text-state-failed-validation-fg">
        Could not load the Flow catalog: {error.message}
      </div>
    );
  }

  if (isLoading || !flows) {
    return (
      <div className={cn("grid gap-3 sm:grid-cols-2", className)}>
        {Array.from({ length: 2 }).map((_, i) => (
          <Skeleton key={i} className="h-32" />
        ))}
      </div>
    );
  }

  if (flows.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-border-subtle bg-surface-2/50 p-8 text-center text-sm text-muted-foreground">
        No Flow templates found in the configured flows directory.
      </div>
    );
  }

  const selectedIdx = flows.findIndex((f) => f.name === selected);
  const focusIdx = selectedIdx === -1 ? 0 : selectedIdx;

  const moveFocus = (target: number): void => {
    const buttons =
      containerRef.current?.querySelectorAll<HTMLButtonElement>("button[role='radio']");
    if (!buttons || buttons.length === 0) return;
    const wrapped = ((target % buttons.length) + buttons.length) % buttons.length;
    buttons[wrapped]?.focus();
    onSelect(flows[wrapped].name);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>, idx: number): void => {
    switch (e.key) {
      case "ArrowRight":
      case "ArrowDown":
        e.preventDefault();
        moveFocus(idx + 1);
        break;
      case "ArrowLeft":
      case "ArrowUp":
        e.preventDefault();
        moveFocus(idx - 1);
        break;
      case "Home":
        e.preventDefault();
        moveFocus(0);
        break;
      case "End":
        e.preventDefault();
        moveFocus(flows.length - 1);
        break;
    }
  };

  return (
    <div
      ref={containerRef}
      className={cn("grid gap-3 sm:grid-cols-2", className)}
      role="radiogroup"
    >
      {flows.map((flow, idx) => {
        const isSelected = selected === flow.name;
        return (
          <button
            key={flow.name}
            type="button"
            role="radio"
            aria-checked={isSelected}
            tabIndex={idx === focusIdx ? 0 : -1}
            onClick={() => onSelect(flow.name)}
            onKeyDown={(e) => onKeyDown(e, idx)}
            className={cn(
              "group flex flex-col gap-2 rounded-lg border bg-card p-card text-left transition-colors",
              isSelected
                ? "border-cyan/60 bg-surface-2 shadow-[inset_2px_0_0_hsl(var(--ring))]"
                : "border-border-subtle hover:border-border-strong hover:bg-surface-2"
            )}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-2">
                <Boxes className="h-4 w-4 text-muted-foreground" aria-hidden />
                <h3 className="font-display text-sm font-semibold tracking-tight text-foreground">
                  {flow.display_name || flow.name}
                </h3>
              </div>
              <span className="rounded-sm bg-surface-2 px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">
                v{flow.version}
              </span>
            </div>
            <p className="font-mono text-[11px] text-muted-foreground">{flow.name}</p>
            {flow.description && (
              <p className="line-clamp-3 text-xs text-muted-foreground">{flow.description}</p>
            )}
            <div className="mt-auto flex flex-wrap gap-1.5 text-[10px]">
              <span className="inline-flex items-center gap-1 rounded-full border border-border-subtle bg-surface-2 px-1.5 py-0.5 text-muted-foreground">
                {flow.node_count} {flow.node_count === 1 ? "node" : "nodes"}
              </span>
            </div>
          </button>
        );
      })}
    </div>
  );
}
